import { useState } from 'react';
import { AppBundle, getImportShim } from '../utils/app-bundles';
import { APP_EXTERNAL_UI_URLS, getAppExternalOverrides } from '../utils/dev-helper';
import { useMountEffect } from './state/use-mount-effect';

type ImportMap = {
  imports: Record<string, string>;
};

type ImportMapLocation = Pick<Location, 'host' | 'hostname' | 'protocol'>;

type SecureStackUrls = {
  designStudioOrigin: string;
  hydraOrigin: string;
  secureOrigin: string;
};

const SECURE_TAILSCALE_STACK_HOST_REGEX = /^(?<stack>.+)-secure(?<domain>\.tail\d+\.ts\.net)$/;
let isDesignStudioSecureStackAssetUrlRewriteInstalled = false;

function getSecureStackUrls(location: ImportMapLocation): SecureStackUrls | null {
  if (location.protocol !== 'https:') {
    return null;
  }

  const match = SECURE_TAILSCALE_STACK_HOST_REGEX.exec(location.hostname);
  if (match?.groups == null) {
    return null;
  }

  return {
    designStudioOrigin: `http://${match.groups.stack}${match.groups.domain}:6109`,
    hydraOrigin: `http://${match.groups.stack}${match.groups.domain}:3002`,
    secureOrigin: `${location.protocol}//${location.host}`,
  };
}

export function getDesignStudioSecureStackImportMapEntries(location: ImportMapLocation = window.location) {
  const urls = getSecureStackUrls(location);
  if (urls == null) {
    return {};
  }

  return {
    [`${urls.designStudioOrigin}/design-studio/`]: '/design-studio/',
  };
}

export function rewriteDesignStudioSecureStackAssetUrl(url: string, location: ImportMapLocation = window.location) {
  const urls = getSecureStackUrls(location);
  if (urls == null) {
    return url;
  }

  const observedRuntimeRewrites: Record<string, string> = {
    'http://localhost:6101/design-studio/api': `${urls.secureOrigin}/design-studio/api`,
    [`${urls.hydraOrigin}/v1`]: `${urls.secureOrigin}/v1`,
  };

  for (const [remoteOrigin, secureOrigin] of Object.entries(observedRuntimeRewrites)) {
    if (url === remoteOrigin) {
      return secureOrigin;
    }

    if (url.startsWith(`${remoteOrigin}/`) || url.startsWith(`${remoteOrigin}?`)) {
      return `${secureOrigin}${url.slice(remoteOrigin.length)}`;
    }
  }

  const designStudioBaseUrl = `${urls.designStudioOrigin}/design-studio/`;
  if (url === `${designStudioBaseUrl}__EXTERNAL_CSS_FILES__`) {
    return 'data:text/css,';
  }

  if (!url.startsWith(designStudioBaseUrl)) {
    return url;
  }

  return `${urls.secureOrigin}/design-studio/${url.slice(designStudioBaseUrl.length)}`;
}

function patchUrlProperty(
  prototype: HTMLImageElement | HTMLLinkElement | HTMLScriptElement,
  property: 'href' | 'src',
  rewriteUrl: (url: string) => string,
) {
  const descriptor = Object.getOwnPropertyDescriptor(prototype, property);
  if (descriptor?.set == null) {
    return;
  }

  Object.defineProperty(prototype, property, {
    ...descriptor,
    set(value: string) {
      descriptor.set?.call(this, rewriteUrl(value));
    },
  });
}

function installDesignStudioSecureStackAssetUrlRewrite(location: ImportMapLocation = window.location) {
  if (isDesignStudioSecureStackAssetUrlRewriteInstalled || getSecureStackUrls(location) == null) {
    return;
  }

  isDesignStudioSecureStackAssetUrlRewriteInstalled = true;

  const rewriteUrl = (url: string) => rewriteDesignStudioSecureStackAssetUrl(url, location);

  patchUrlProperty(HTMLLinkElement.prototype, 'href', rewriteUrl);
  patchUrlProperty(HTMLScriptElement.prototype, 'src', rewriteUrl);
  patchUrlProperty(HTMLImageElement.prototype, 'src', rewriteUrl);

  const originalSetAttribute = Element.prototype.setAttribute;
  Element.prototype.setAttribute = function setAttribute(name: string, value: string) {
    if ((name === 'href' || name === 'src') && typeof value === 'string') {
      originalSetAttribute.call(this, name, rewriteUrl(value));
      return;
    }

    originalSetAttribute.call(this, name, value);
  };

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    if (typeof input === 'string') {
      return await originalFetch(rewriteUrl(input), init);
    }

    if (input instanceof URL) {
      return await originalFetch(new URL(rewriteUrl(input.href)), init);
    }

    if (input instanceof Request) {
      return await originalFetch(new Request(rewriteUrl(input.url), input), init);
    }

    return await originalFetch(input, init);
  };
}

export function useInjectImportShim() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  if (error != null) {
    throw error;
  }

  useMountEffect(() => {
    async function load() {
      const importShim = getImportShim();
      installDesignStudioSecureStackAssetUrlRewrite();
      const importMap: ImportMap = {
        imports: {
          [AppBundle.Journeys]:
            import.meta.env.VITE_JOURNEYS_ENTRYPOINT_JS_URL ?? 'http://localhost:3002/entrypoint.js',
          [AppBundle.DesignStudio]:
            import.meta.env.VITE_DESIGN_STUDIO_ENTRYPOINT_JS_URL ?? 'http://localhost:3002/design-studio/entrypoint.js',
          ...getDesignStudioSecureStackImportMapEntries(),
        },
      };

      if (import.meta.env.DEV === true || import.meta.env.VITE_ENABLE_EXTERNAL_UI_OVERRIDES === 'true') {
        const externalUiOverrides = getAppExternalOverrides();
        for (const [app, enabled] of Object.entries(externalUiOverrides)) {
          if (enabled === true && app in importMap.imports) {
            (importMap.imports as Record<string, string>)[app] = APP_EXTERNAL_UI_URLS[app as AppBundle];
          }
        }
      }

      importShim.addImportMap(importMap);

      if (import.meta.env.VITE_ENABLE_EXTERNAL_UI_OVERRIDES === 'true') {
        // TODO: Figure out if we can actually import the refresh runtime
        // correctly. We can't in production-like builds, due to
        // https://github.com/facebook/react/blob/ce6842d8f528977119b80d969306c8475099f66e/packages/react-refresh/src/ReactFreshRuntime.js#L38-L42
        // const runtime = await import('react-refresh/runtime');
        // runtime.injectIntoGlobalHook(window);
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        (window as unknown as { $RefreshReg$: () => void }).$RefreshReg$ = () => {};
        (window as unknown as { $RefreshSig$: () => (type: unknown) => unknown }).$RefreshSig$ =
          () => (type: unknown) =>
            type;
        // This is needed in case a developer runs the external Hydra UI, but
        // works on a vite-built React app on localhost. Much like in the
        // scenario described at https://vitejs.dev/guide/backend-integration.html
        (
          window as unknown as { __vite_plugin_react_preamble_installed__: boolean }
        ).__vite_plugin_react_preamble_installed__ = true;
      }

      setIsLoaded(true);
    }

    load().catch((loadError: unknown) => {
      setError(loadError instanceof Error ? loadError : new Error('Failed to inject embedded app import map.'));
    });
  });

  return isLoaded;
}
